import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Bell,
  Warning,
  CaretDown,
  Calendar,
  MapPin,
  Speedometer,
  Circle,
  Eye,
} from '@phosphor-icons/react';
import { alarmsAPI, devicesAPI } from '@/services/api';
import { format, subDays } from 'date-fns';
import { es } from 'date-fns/locale';

// Alarm types mapping
const alarmTypes = {
  1: { name: 'SOS', severity: 'high', icon: Warning },
  2: { name: 'Corte de energía', severity: 'high', icon: Warning },
  3: { name: 'Vibración', severity: 'medium', icon: Warning },
  4: { name: 'Entrada en geocerca', severity: 'low', icon: MapPin },
  5: { name: 'Salida de geocerca', severity: 'low', icon: MapPin },
  6: { name: 'Exceso de velocidad', severity: 'high', icon: Speedometer },
  7: { name: 'Desplazamiento', severity: 'medium', icon: Warning },
  8: { name: 'Remolque', severity: 'medium', icon: Warning },
  9: { name: 'Batería baja', severity: 'medium', icon: Warning },
  10: { name: 'Fallo GPS', severity: 'low', icon: Warning },
};

const Alerts = () => {
  const navigate = useNavigate();
  
  const [devices, setDevices] = useState([]);
  const [selectedDevice, setSelectedDevice] = useState('all');
  const [alarms, setAlarms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [startDate, setStartDate] = useState(format(subDays(new Date(), 7), 'yyyy-MM-dd'));
  const [endDate, setEndDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [selectedAlarm, setSelectedAlarm] = useState(null);

  // Fetch devices
  useEffect(() => {
    const fetchDevices = async () => {
      try {
        const response = await devicesAPI.getAll();
        setDevices(response.data || []);
      } catch (err) {
        console.error('Error fetching devices:', err);
      }
    };
    fetchDevices();
  }, []);

  // Fetch alarms
  const fetchAlarms = async () => {
    setLoading(true);
    try {
      const beginTime = `${startDate} 00:00:00`;
      const endTimeStr = `${endDate} 23:59:59`;
      
      if (selectedDevice === 'all') {
        // Fetch alarms for all devices
        const allAlarms = [];
        for (const device of devices.slice(0, 10)) { // Limit to first 10 devices
          try {
            const response = await alarmsAPI.getAlarms(device.imei, beginTime, endTimeStr);
            const deviceAlarms = (response.data || []).map(a => ({
              ...a,
              deviceName: device.deviceName || device.imei,
              imei: device.imei,
            }));
            allAlarms.push(...deviceAlarms);
          } catch (e) {
            console.error(`Error fetching alarms for ${device.imei}:`, e);
          }
        }
        // Sort by time
        allAlarms.sort((a, b) => new Date(b.alarmTime) - new Date(a.alarmTime));
        setAlarms(allAlarms);
      } else {
        const response = await alarmsAPI.getAlarms(selectedDevice, beginTime, endTimeStr);
        const device = devices.find(d => d.imei === selectedDevice);
        const deviceAlarms = (response.data || []).map(a => ({
          ...a,
          deviceName: device?.deviceName || selectedDevice,
          imei: selectedDevice,
        }));
        setAlarms(deviceAlarms);
      }
    } catch (err) {
      console.error('Error fetching alarms:', err);
      setAlarms([]);
    } finally {
      setLoading(false);
    }
  };

  // Get alarm info
  const getAlarmInfo = (alarmTypeId) => {
    return alarmTypes[alarmTypeId] || { name: 'Alarma', severity: 'medium', icon: Warning };
  };

  // Severity counts
  const severityCounts = {
    high: alarms.filter(a => getAlarmInfo(a.alarmTypeId).severity === 'high').length,
    medium: alarms.filter(a => getAlarmInfo(a.alarmTypeId).severity === 'medium').length,
    low: alarms.filter(a => getAlarmInfo(a.alarmTypeId).severity === 'low').length,
  };

  return (
    <div className="min-h-screen bg-gray-100" data-testid="alerts-page">
      {/* Header */}
      <header className="h-16 border-b border-gray-200 flex items-center justify-between px-4 bg-white/95 backdrop-blur sticky top-0 z-10">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/')}
            className="p-2 rounded-lg hover:bg-gray-200 transition-colors"
            data-testid="back-btn"
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="font-bold flex items-center gap-2">
              <Bell size={20} className="text-primary" />
              Alertas
            </h1>
            <p className="text-xs text-gray-600">
              Historial de alarmas y notificaciones
            </p>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto p-6 space-y-6">
        {/* Filters */}
        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Device Select */}
            <div>
              <label className="text-xs text-gray-600 font-medium mb-1 block">Dispositivo</label>
              <div className="relative">
                <select
                  value={selectedDevice}
                  onChange={(e) => setSelectedDevice(e.target.value)}
                  data-testid="device-select"
                  className="w-full h-10 px-3 pr-8 rounded-lg bg-white border border-gray-300 text-sm text-gray-800 appearance-none"
                >
                  <option value="all">Todos los dispositivos</option>
                  {devices.map(d => (
                    <option key={d.imei} value={d.imei}>
                      {d.deviceName || d.imei}
                    </option>
                  ))}
                </select>
                <CaretDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-500" />
              </div>
            </div>

            {/* Start Date */}
            <div>
              <label className="text-xs text-gray-600 font-medium mb-1 block">Fecha inicio</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                data-testid="start-date-input"
                className="w-full h-10 px-3 rounded-lg bg-white border border-gray-300 text-sm text-gray-800"
              />
            </div>

            {/* End Date */}
            <div>
              <label className="text-xs text-gray-600 font-medium mb-1 block">Fecha fin</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                data-testid="end-date-input"
                className="w-full h-10 px-3 rounded-lg bg-white border border-gray-300 text-sm text-gray-800"
              />
            </div>

            {/* Search Button */}
            <div className="flex items-end">
              <button
                onClick={fetchAlarms}
                disabled={loading}
                data-testid="fetch-alarms-btn"
                className="w-full h-10 rounded-lg bg-primary text-primary-foreground font-medium hover:bg-primary/90 disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
              >
                {loading ? (
                  <Circle size={18} className="spinner" />
                ) : (
                  <>
                    <Bell size={18} /> Buscar
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white border border-gray-200 rounded-xl p-4">
            <div className="text-sm text-gray-500 mb-1">Total Alertas</div>
            <div className="text-3xl font-bold">{alarms.length}</div>
          </div>
          <div className="bg-white border-l-4 border-destructive rounded-xl p-4">
            <div className="text-sm text-gray-500 mb-1">Alta Prioridad</div>
            <div className="text-3xl font-bold text-destructive">{severityCounts.high}</div>
          </div>
          <div className="bg-white border-l-4 border-yellow-500 rounded-xl p-4">
            <div className="text-sm text-gray-500 mb-1">Media Prioridad</div>
            <div className="text-3xl font-bold text-yellow-500">{severityCounts.medium}</div>
          </div>
          <div className="bg-white border-l-4 border-primary rounded-xl p-4">
            <div className="text-sm text-gray-500 mb-1">Baja Prioridad</div>
            <div className="text-3xl font-bold text-primary">{severityCounts.low}</div>
          </div>
        </div>

        {/* Alerts List */}
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Circle size={32} className="spinner text-primary" />
            </div>
          ) : alarms.length > 0 ? (
            <div className="divide-y divide-border">
              {alarms.map((alarm, i) => {
                const alarmInfo = getAlarmInfo(alarm.alarmTypeId);
                const AlarmIcon = alarmInfo.icon;
                
                return (
                  <div 
                    key={i} 
                    className={`p-4 hover:bg-gray-50 transition-colors cursor-pointer ${
                      alarmInfo.severity === 'high' ? 'alert-high' :
                      alarmInfo.severity === 'medium' ? 'alert-medium' : 'alert-low'
                    }`}
                    onClick={() => setSelectedAlarm(selectedAlarm === i ? null : i)}
                    data-testid={`alarm-item-${i}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        <div className={`p-2 rounded-lg ${
                          alarmInfo.severity === 'high' ? 'bg-destructive/20 text-destructive' :
                          alarmInfo.severity === 'medium' ? 'bg-yellow-500/20 text-yellow-500' :
                          'bg-primary/20 text-primary'
                        }`}>
                          <AlarmIcon size={20} />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-medium">
                              {alarm.alarmTypeNameEn || alarmInfo.name}
                            </span>
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                              alarmInfo.severity === 'high' ? 'bg-destructive/20 text-destructive' :
                              alarmInfo.severity === 'medium' ? 'bg-yellow-500/20 text-yellow-500' :
                              'bg-primary/20 text-primary'
                            }`}>
                              {alarmInfo.severity === 'high' ? 'Alta' : 
                               alarmInfo.severity === 'medium' ? 'Media' : 'Baja'}
                            </span>
                          </div>
                          <p className="text-sm text-gray-500 mt-0.5">
                            {alarm.deviceName}
                          </p>
                          <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                            <Calendar size={12} />
                            {alarm.alarmTime ? format(new Date(alarm.alarmTime), 'dd/MM/yyyy HH:mm:ss', { locale: es }) : '-'}
                          </p>
                        </div>
                      </div>
                      
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/tracks/${alarm.imei}`);
                        }}
                        className="p-2 rounded-lg hover:bg-gray-200 transition-colors"
                        data-testid={`view-location-btn-${i}`}
                      >
                        <Eye size={18} />
                      </button>
                    </div>
                    
                    {/* Expanded Details */}
                    {selectedAlarm === i && (
                      <div className="mt-3 pt-3 border-t border-gray-200 space-y-2 fade-in">
                        {alarm.address && (
                          <p className="text-sm flex items-start gap-2">
                            <MapPin size={14} className="mt-1 text-gray-500" />
                            <span>{alarm.address}</span>
                          </p>
                        )}
                        {alarm.speed !== undefined && (
                          <p className="text-sm flex items-center gap-2">
                            <Speedometer size={14} className="text-gray-500" />
                            <span>Velocidad: {alarm.speed} km/h</span>
                          </p>
                        )}
                        {(alarm.lat && alarm.lng) && (
                          <p className="text-xs text-gray-600">
                            Coordenadas: {alarm.lat}, {alarm.lng}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-16 text-gray-500">
              <Bell size={48} className="mb-4" />
              <p className="font-medium">Sin alertas en este período</p>
              <p className="text-sm">Selecciona un dispositivo y rango de fechas</p>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default Alerts;
